import React, { useState } from 'react';
import Nav from './Nav';
import { Mail, Phone, MapPin, MessageSquare, Clock, Facebook, Instagram, Twitter } from 'lucide-react';
import { motion } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa6';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState({
    type: null,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    // ... keep existing code (form submission logic)
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-200 via-pink-100 to-pink-200">
      <Nav />
      
      <div className="container mx-auto px-4 py-12">
        <motion.div 
          className="max-w-6xl mx-auto"
          initial="initial"
          animate="animate"
          variants={{
            initial: { opacity: 0 },
            animate: { opacity: 1, transition: { staggerChildren: 0.2 } }
          }}
        >
          {/* Hero Section */}
          <motion.div 
            className="text-center mb-16"
            variants={fadeIn}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Get in Touch
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We're here to help and answer any questions you might have. We look forward to hearing from you!
            </p>
          </motion.div>

          {/* Contact Information Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Phone className="w-6 h-6 text-pink-500" />,
                title: "Call Us",
                content: "+1 (123) 456-7890",
                subContent: "Available 24/7 for urgent inquiries"
              },
              {
                icon: <Mail className="w-6 h-6 text-pink-500" />,
                title: "Email Us",
                content: "support@example.com",
                subContent: "We usually respond within 24 hours"
              },
              {
                icon: <FaWhatsapp className="w-6 h-6 text-pink-500" />,
                title: "Whatsapp",
                content: "+91 987xxxxxxx",
                subContent: "We usually respond within 24 hours"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                variants={fadeIn}
                whileHover={{ scale: 1.03 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="bg-pink-100 w-14 h-14 rounded-full flex items-center justify-center mb-6">
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{item.title}</h3>
                <p className="text-gray-600 mb-2">{item.content}</p>
                <p className="text-sm text-gray-500">{item.subContent}</p>
              </motion.div>
            ))}
          </div>

          {/* Contact Form and Map Section */}
          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <motion.div 
              className="bg-white rounded-2xl shadow-lg p-8"
              variants={fadeIn}
            >
              <div className="flex items-center mb-6">
                <div className="bg-pink-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                  <MessageSquare className="w-6 h-6 text-pink-500" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-800">
                  Send Us a Message
                </h2>
              </div>
              
              {status.type && (
                <div className={`mb-6 p-4 rounded-lg ${
                  status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {status.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-200 focus:border-pink-400 transition-colors"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-200 focus:border-pink-400 transition-colors"
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-pink-200 focus:border-pink-400 transition-colors"
                    placeholder="How can we help you?"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-pink-500 text-white py-3 px-6 rounded-lg hover:bg-pink-600 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 ${
                    isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </motion.div>

            {/* Location Information */}
            <motion.div 
              className="bg-white rounded-2xl shadow-lg p-8"
              variants={fadeIn}
            >
              <div className="flex items-center mb-6">
                <div className="bg-pink-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                  <MapPin className="w-6 h-6 text-pink-500" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-800">
                  Visit Our Store
                </h2>
              </div>
              
              <div className="space-y-6">
                <div className="rounded-lg overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80" 
                    alt="Store exterior" 
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src="https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?auto=format&fit=crop&w=400&q=80" 
                      alt="Store interior" 
                      className="w-full h-32 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="rounded-lg overflow-hidden">
                    <img 
                      src="https://images.unsplash.com/photo-1508997449629-303059a039c0?auto=format&fit=crop&w=400&q=80" 
                      alt="Store showcase" 
                      className="w-full h-32 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Flagship Store
                  </h3>
                  <p className="text-gray-600">
                    123 Fashion Avenue<br />
                    New York, NY 10001<br />
                    United States
                  </p>
                  <div className="flex space-x-4">
                    <a href="#" className="text-pink-500 hover:text-pink-600">
                      <FaWhatsapp className="w-6 h-6" />
                    </a>
                    <a href="#" className="text-pink-500 hover:text-pink-600">
                      <Instagram className="w-6 h-6" />
                    </a>
                    <a href="#" className="text-pink-500 hover:text-pink-600">
                      <Facebook className="w-6 h-6" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContactUs;